import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from 'recharts';
import { TrendingUp, Calendar, Target, Award } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  not_started: '#94a3b8',
  in_progress: '#F59E0B',
  completed: '#10B981',
  blocked: '#EF4444',
};

const STATUS_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  blocked: 'Blocked',
};

const RATING_COLORS = ['#EF4444', '#F59E0B', '#94a3b8', '#3B82F6', '#10B981'];

const AnalyticsSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/analytics`, { withCredentials: true });
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) return null;

  const goalPieData = data.goal_stats.map((g) => ({
    name: STATUS_LABELS[g.status] || g.status,
    value: g.count,
    color: STATUS_COLORS[g.status] || '#94a3b8',
  }));

  return (
    <div className="space-y-6" data-testid="analytics-section">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-200" data-testid="metric-avg-rating">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <Award className="w-5 h-5 text-blue-600" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Avg Rating</span>
            </div>
            <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {data.avg_rating}/5
            </p>
            <p className="text-sm text-slate-600 mt-1">{data.total_performances} reviews</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200" data-testid="metric-avg-progress">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-5 h-5 text-blue-600" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Avg Progress</span>
            </div>
            <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {data.goal_avg_progress}%
            </p>
            <p className="text-sm text-slate-600 mt-1">Across {data.goal_total} goals</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200" data-testid="metric-completed-goals">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Completed Goals</span>
            </div>
            <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {data.goal_stats.find((g) => g.status === 'completed')?.count || 0}
            </p>
            <p className="text-sm text-slate-600 mt-1">of {data.goal_total} total</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200" data-testid="metric-total-leaves">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Total Leaves</span>
            </div>
            <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {data.leave_trends.reduce((s, m) => s + (m.pending || 0) + (m.approved || 0) + (m.rejected || 0), 0)}
            </p>
            <p className="text-sm text-slate-600 mt-1">Across {data.leave_trends.length} months</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200" data-testid="chart-leave-trends">
          <CardHeader>
            <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Leave Trends</CardTitle>
            <CardDescription>Leave applications by month and status</CardDescription>
          </CardHeader>
          <CardContent>
            {data.leave_trends.length === 0 ? (
              <div className="text-center py-12 text-slate-500">No leave data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.leave_trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" style={{ fontSize: 12 }} />
                  <YAxis stroke="#64748b" style={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="pending" fill="#F59E0B" name="Pending" />
                  <Bar dataKey="approved" fill="#10B981" name="Approved" />
                  <Bar dataKey="rejected" fill="#EF4444" name="Rejected" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200" data-testid="chart-goal-status">
          <CardHeader>
            <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Goal Status Distribution</CardTitle>
            <CardDescription>Breakdown of teacher goals by status</CardDescription>
          </CardHeader>
          <CardContent>
            {data.goal_total === 0 ? (
              <div className="text-center py-12 text-slate-500">No goals data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={goalPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={4}
                    dataKey="value"
                    label={(entry) => `${entry.name}: ${entry.value}`}
                  >
                    {goalPieData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200 lg:col-span-2" data-testid="chart-rating-distribution">
          <CardHeader>
            <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Performance Rating Distribution</CardTitle>
            <CardDescription>How teachers are performing across all reviews</CardDescription>
          </CardHeader>
          <CardContent>
            {data.total_performances === 0 ? (
              <div className="text-center py-12 text-slate-500">No performance review data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.rating_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="rating" stroke="#64748b" tickFormatter={(v) => `${v} ★`} style={{ fontSize: 12 }} />
                  <YAxis stroke="#64748b" style={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Teachers">
                    {data.rating_distribution.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={RATING_COLORS[idx] || '#3B82F6'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsSection;
